import { BASE_URL } from '../../../configs'
import { createJsonHeaders, logoutOnRequestOrResponseJson } from '../../../api'

const packageUrl = (botId, suffix = '') => `${BASE_URL}/bot/${botId}/package${suffix}`

const parseEnvelope = json => ({
  message: json?.message || '',
  data: json?.data ?? null,
  meta: json?.meta ?? null,
})

const request = (botId, suffix, { method = 'GET', body, signal } = {}) =>
  fetch(packageUrl(botId, suffix), {
    credentials: 'include',
    headers: createJsonHeaders(),
    method,
    signal,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  }).then(response => logoutOnRequestOrResponseJson(response).then(parseEnvelope))

/**
 * Current package assignment for a QCS bot. 404 means none yet — not an error
 * for the UI, so this returns null instead of throwing.
 */
export function getBotPackage(botId, signal) {
  return fetch(packageUrl(botId), {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
    signal,
  }).then(response => {
    if (response.status === 404) return null
    return logoutOnRequestOrResponseJson(response).then(json => parseEnvelope(json).data)
  })
}

export function assignBotPackage(botId, packageId) {
  return request(botId, '', { method: 'POST', body: { packageId: Number(packageId) || packageId } }).then(
    ({ data, message }) => {
      if (!data) throw new Error(message || 'Package could not be assigned.')
      return data
    },
  )
}

export function changeBotPackage(botId, packageId) {
  return request(botId, '', { method: 'PATCH', body: { packageId: Number(packageId) || packageId } }).then(
    ({ data, message }) => {
      if (!data) throw new Error(message || 'Package could not be changed.')
      return data
    },
  )
}

export function updateBotPackageConfig(botId, patch) {
  return request(botId, '/config', { method: 'PATCH', body: patch }).then(({ data, message }) => {
    if (!data) throw new Error(message || 'Package assignment could not be updated.')
    return data
  })
}

/**
 * Customer ask for a higher care-plan-audit cap. Does not change the limit.
 * Body is `{ message, limit }` per the package assignment API.
 */
export function requestPackageUsageLimitIncrease(botId, { message, limit }) {
  return request(botId, '/usage-limit/increase-request', {
    method: 'POST',
    body: { message, limit },
  }).then(({ data, message: okMessage }) => ({ data, message: okMessage }))
}

/**
 * Email-only request to change supervision config. Does not update assignment.
 * Body: `{ note?, supervision: { defaultEnabled, dynamicEnabled } }`
 */
export function requestSupervisionChange(botId, { note, supervision }) {
  return request(botId, '/supervision/change-request', {
    method: 'POST',
    body: {
      ...(note != null && String(note).trim() ? { note: String(note).trim() } : {}),
      supervision,
    },
  }).then(({ data, message }) => ({ data, message }))
}

/** Customer ask to add Care Plan Audit to the package. No request body. */
export function requestCarePlanEnable(botId) {
  return request(botId, '/care-plan/enable-request', { method: 'POST' }).then(({ data, message }) => ({
    data,
    message,
  }))
}

/** Customer ask to add Supervision to the package. No request body. */
export function requestSupervisionEnable(botId) {
  return request(botId, '/supervision/enable-request', { method: 'POST' }).then(({ data, message }) => ({
    data,
    message,
  }))
}

/** First assign, or change if one already exists (409 → PATCH). */
export function assignOrChangeBotPackage(botId, packageId) {
  return assignBotPackage(botId, packageId).catch(err => {
    const text = String(err?.message || '')
    if (/already/i.test(text) || /409/.test(text)) {
      return changeBotPackage(botId, packageId)
    }
    throw err
  })
}
